<script setup lang="ts">
import type { ISelectItem } from "@/types"
import { computed, watch } from "vue"
import { Button, Drawer, Select } from "@/components/ui"
import { useTranslation } from "@/composables"
import { FlaggedContentStatus, FlaggedContentType } from "@/models"
import { FlaggedContentFiltersDrawerKey } from "@/symbols"

const emit = defineEmits<{
  filtersChanged: []
}>()

const { t } = useTranslation()

const status = defineModel<FlaggedContentStatus | undefined>("status")
const contentType = defineModel<FlaggedContentType | undefined>("contentType")
const perPage = defineModel("perPage", { type: Number })

const hasActiveFilters = computed(
  () => status.value != null || contentType.value != null,
)

function clearFilters() {
  status.value = undefined
  contentType.value = undefined
}

const perPageOptions: ISelectItem<number>[] = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
]

const statusOptions: ISelectItem<FlaggedContentStatus>[] = [
  { label: t("flagged_content_filters.status_options.pending_review"), value: FlaggedContentStatus.PENDING },
  { label: t("flagged_content_filters.status_options.hidden"), value: FlaggedContentStatus.APPROVED },
  { label: t("flagged_content_filters.status_options.restored"), value: FlaggedContentStatus.REJECTED },
]

const contentTypeOptions: ISelectItem<FlaggedContentType>[] = [
  { label: t("flagged_content_filters.content_type_options.photo"), value: FlaggedContentType.PHOTO },
  { label: t("flagged_content_filters.content_type_options.video"), value: FlaggedContentType.VIDEO },
  { label: t("flagged_content_filters.content_type_options.message"), value: FlaggedContentType.MESSAGE },
  { label: t("flagged_content_filters.content_type_options.comment"), value: FlaggedContentType.COMMENT },
]

watch([status, contentType, perPage], () => {
  emit("filtersChanged")
})
</script>

<template>
  <Drawer :name="FlaggedContentFiltersDrawerKey" title="Filters">
    <form @submit.prevent>
      <div class="flex flex-col gap-4">
        <Select
          :model-value="perPage"
          :items="perPageOptions"
          name="perPage"
          :label="t('flagged_content_filters.per_page_label')"
          @update:model-value="(value: number) => perPage = value"
        />
        <Select
          :model-value="status"
          :items="statusOptions"
          name="status"
          :label="t('flagged_content_filters.review_status_label')"
          clearable
          @update:model-value="(value: FlaggedContentStatus | undefined) => status = value"
        />
        <Select
          :model-value="contentType"
          :items="contentTypeOptions"
          name="contentType"
          :label="t('flagged_content_filters.content_type_label')"
          clearable
          @update:model-value="(value: FlaggedContentType | undefined) => contentType = value"
        />
      </div>
    </form>
    <template #footer>
      <div class="flex px-6 py-4">
        <Button
          prefix-icon="Close"
          variant="tertiary"
          class="w-full"
          :disabled="!hasActiveFilters"
          data-test="flagged-content-clear-filters"
          @click="clearFilters"
        >
          {{ t("flagged_content_filters.clear_filters") }}
        </Button>
      </div>
    </template>
  </Drawer>
</template>
